import { useState } from "react";
import { useAuthContext } from "@/hooks/useAuthContext";
import {
  useGetOfficeMembersQuery,
  useSendInvitationMutation,
  useCancelInvitationMutation,
  useGetInvitationsQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
} from "@/redux/api/offices.rtkq";
import {
  useListCasesQuery,
  useBulkAssignCasesMutation,
} from "@/redux/api/cases.rtkq";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const OfficeMembersPage = () => {
  const { getAccountId, isAccountAdmin } = useAuthContext();
  const officeId = getAccountId() || "";
  const isMobile = useIsMobile();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("office_user");
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const { data: membersData, isLoading } = useGetOfficeMembersQuery(officeId, {
    skip: !officeId,
  });
  const { data: invitations } = useGetInvitationsQuery(officeId, {
    skip: !officeId || !isAccountAdmin(),
  });
  const [sendInvitation] = useSendInvitationMutation();
  const [cancelInvitation] = useCancelInvitationMutation();
  const [updateRole] = useUpdateMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await sendInvitation({ officeId, email: inviteEmail.trim(), role: inviteRole }).unwrap();
      setInviteEmail("");
    } catch (err: any) {
      alert(err.data?.message || "שגיאה בשליחת ההזמנה");
    }
  };

  const selectedMember = membersData?.members.find((m) => m.userId === selectedMemberId);

  if (isLoading) return <div className="p-6 text-muted-foreground">טוען...</div>;

  const casesPanel = selectedMember ? (
    <MemberCasesPanel
      officeId={officeId}
      userId={selectedMember.userId}
      displayName={selectedMember.displayName || selectedMember.email}
      className={isMobile ? "w-full border-0 shadow-none" : undefined}
    />
  ) : null;

  return (
    <div className="container mx-auto max-w-5xl p-4 md:p-6" dir="rtl">
      <h1 className="mb-6 text-2xl font-bold text-foreground">חברי משרד</h1>

      {isAccountAdmin() && (
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-lg font-semibold">הזמנת משתמש</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              placeholder="כתובת אימייל"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            >
              <option value="office_user">משתמש</option>
              <option value="office_admin">מנהל</option>
            </select>
            <button
              onClick={handleInvite}
              className="rounded-lg bg-chen-900 px-4 py-2 text-white hover:opacity-90"
            >
              שלח הזמנה
            </button>
          </div>
        </div>
      )}

      {invitations && invitations.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold">הזמנות ממתינות</h2>
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="mb-2 flex flex-col gap-2 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <span className="text-foreground">{inv.email}</span>
                <span className="me-2 text-sm text-muted-foreground">
                  ({inv.role === "office_admin" ? "מנהל" : "משתמש"})
                </span>
              </div>
              <button
                onClick={() => cancelInvitation({ officeId, invitationId: inv.id })}
                className="self-start text-sm text-red-500 hover:text-red-700"
              >
                ביטול
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-6 md:flex-row">
        <div className="min-w-0 flex-1">
          <h2 className="mb-3 text-lg font-semibold">
            חברים ({membersData?.members.length || 0})
          </h2>
          {membersData?.members.map((member) => (
            <div
              key={member.userId}
              onClick={() =>
                isAccountAdmin() &&
                setSelectedMemberId(selectedMemberId === member.userId ? null : member.userId)
              }
              className={`mb-2 flex flex-col gap-3 rounded-lg border bg-card p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                isAccountAdmin() ? "cursor-pointer hover:border-chen-900/50" : ""
              } ${selectedMemberId === member.userId ? "border-chen-900" : "border-border"}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-foreground">
                  {member.displayName || member.email}
                  {member.isOwner && (
                    <span className="me-2 rounded-full bg-chen-900 px-2 py-0.5 text-xs text-white">
                      בעלים
                    </span>
                  )}
                </div>
                <div className="truncate text-sm text-muted-foreground">{member.email}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isAccountAdmin() && !member.isOwner && (
                  <>
                    <select
                      value={member.role}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        updateRole({ officeId, userId: member.userId, role: e.target.value })
                      }
                      className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground"
                    >
                      <option value="office_user">משתמש</option>
                      <option value="office_admin">מנהל</option>
                    </select>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`להסיר את ${member.displayName || member.email}?`))
                          removeMember({ officeId, userId: member.userId });
                      }}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      הסר
                    </button>
                  </>
                )}
                {!isAccountAdmin() && (
                  <span className="text-sm text-muted-foreground">
                    {member.role === "office_admin" ? "מנהל" : "משתמש"}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {isAccountAdmin() && selectedMember && (
          isMobile ? (
            <Sheet
              open={!!selectedMemberId}
              onOpenChange={(open) => {
                if (!open) setSelectedMemberId(null);
              }}
            >
              <SheetContent
                side="bottom"
                className="flex h-[min(85dvh,40rem)] flex-col gap-0 p-0 pb-[env(safe-area-inset-bottom)]"
              >
                <SheetHeader className="border-b border-border px-4 py-3 text-start">
                  <SheetTitle>שיוך תיקים</SheetTitle>
                </SheetHeader>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">{casesPanel}</div>
              </SheetContent>
            </Sheet>
          ) : (
            casesPanel
          )
        )}
      </div>
    </div>
  );
};

const MemberCasesPanel = ({
  officeId,
  userId,
  displayName,
  className,
}: {
  officeId: string;
  userId: string;
  displayName: string;
  className?: string;
}) => {
  const { data: casesData, isLoading } = useListCasesQuery(
    { officeId, limit: 100 },
    { skip: !officeId }
  );
  const [bulkAssign, { isLoading: bulkLoading }] = useBulkAssignCasesMutation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [permission, setPermission] = useState("viewer");
  const [caseSearch, setCaseSearch] = useState("");

  const allCases = casesData?.cases || [];
  const filtered = caseSearch
    ? allCases.filter(
        (c) =>
          c.title.toLowerCase().includes(caseSearch.toLowerCase()) ||
          String(c.caseNumber || "").includes(caseSearch)
      )
    : allCases;

  const toggleCase = (caseId: string) => {
    const next = new Set(selected);
    if (next.has(caseId)) next.delete(caseId);
    else next.add(caseId);
    setSelected(next);
  };

  const handleBulkAssign = async () => {
    if (selected.size === 0) return;
    const assignments = Array.from(selected).map((caseId) => ({
      caseId,
      userId,
      permission,
    }));
    try {
      const result = await bulkAssign({ officeId, assignments }).unwrap();
      const created = result.results.filter((r) => r.status === "created").length;
      const updated = result.results.filter((r) => r.status === "updated").length;
      const errors = result.results.filter((r) => r.status === "error").length;
      alert(
        `שויכו ${created} תיקים חדשים, עודכנו ${updated}${errors > 0 ? `, שגיאות: ${errors}` : ""}`
      );
      setSelected(new Set());
    } catch {
      alert("שגיאה בשיוך");
    }
  };

  return (
    <div
      className={
        className ??
        "sticky top-6 h-fit w-80 rounded-lg border border-border bg-card p-4"
      }
    >
      <h3 className="mb-1 text-sm font-semibold">שיוך תיקים ל-{displayName}</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        סמן תיקים ובחר הרשאה לשיוך מרוכז
      </p>

      <input
        type="text"
        placeholder="חפש תיק..."
        value={caseSearch}
        onChange={(e) => setCaseSearch(e.target.value)}
        className="mb-2 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-foreground"
      />

      <div className="mb-3 flex items-center gap-2">
        <select
          value={permission}
          onChange={(e) => setPermission(e.target.value)}
          className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs"
        >
          <option value="viewer">צפייה</option>
          <option value="contributor">תורם</option>
          <option value="editor">עורך</option>
        </select>
        <button
          onClick={handleBulkAssign}
          disabled={selected.size === 0 || bulkLoading}
          className="rounded-lg bg-chen-900 px-3 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
        >
          {bulkLoading ? "..." : `שייך (${selected.size})`}
        </button>
      </div>

      <div className="max-h-96 space-y-1 overflow-y-auto">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-muted-foreground">טוען...</p>
        ) : filtered.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">לא נמצאו תיקים</p>
        ) : (
          filtered.map((c) => (
            <label
              key={c.caseId}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
            >
              <input
                type="checkbox"
                checked={selected.has(c.caseId)}
                onChange={() => toggleCase(c.caseId)}
                className="rounded"
              />
              <span className="flex-1 truncate">{c.title}</span>
              {c.caseNumber && (
                <span className="shrink-0 text-xs text-muted-foreground">#{c.caseNumber}</span>
              )}
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export default OfficeMembersPage;
